// src/components/email-form.tsx

"use client"

import { logClientError } from '~/lib/logger-client'
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "~/components/Button"
import { Form, FormControl, FormField, FormItem } from "~/components/Form"
import { Input } from "~/components/Input"
import { EventTypes, sendEvent, trackEmailCapture } from "~/lib/gtag"
import { useToast } from "~/lib/use-toast"
import { addToCRM } from "~/server/actions/crm-integration"
import { sendEmail } from "~/server/actions/send-email"
import { isLikelyLeadSpamEmail } from "~/lib/validation/email-domains"

/**
 * Email form validation schema using Zod 4.
 * 
 * @description Validates email addresses with comprehensive checks including:
 * - Required field validation
 * - Email format validation
 * - Common typo detection (.con -> .com)
 * - Domain validation
 * - Length constraints
 * - Numeric-only local part rejection
 * - Shared placeholder/disposable/generated-lead classifier (isLikelyLeadSpamEmail)
 * 
 * @remarks
 * Updated for Zod 4:
 * - Uses `error` parameter instead of deprecated `message`
 * - Uses `.check()` for custom validations instead of `.refine()`
 * - Leverages new `z.email()` top-level API
 */
const formSchema = z.object({
  email: z.string()
    .trim()
    .min(1, { error: 'Email address is required' })
    .check(z.email({ error: 'Please enter a valid email address' }))
    .transform(email => email.toLowerCase())
    // Check for common typo: .con instead of .com
    .check((ctx) => {
      if (ctx.value.endsWith('.con')) {
        ctx.issues.push({
          code: 'custom',
          message: 'Did you mean .com?',
          input: ctx.value
        })
      }
    })
    // Validate email domain structure
    .check((ctx) => {
      const domain = ctx.value.split('@')[1]
      if (!domain || domain.length < 2 || !domain.includes('.')) {
        ctx.issues.push({
          code: 'custom',
          message: 'Invalid email domain',
          input: ctx.value
        })
      }
    })
    // Enforce maximum email length per RFC 5321
    .check((ctx) => {
      if (ctx.value.length > 254) {
        ctx.issues.push({
          code: 'custom',
          message: 'Email address is too long',
          input: ctx.value
        })
      }
    })
    // Reject emails with numeric-only local parts
    .check((ctx) => {
      const localPart = ctx.value.split('@')[0]
      if (!localPart || localPart.length < 1 || /^[0-9]+$/.test(localPart)) {
        ctx.issues.push({
          code: 'custom',
          message: 'Email cannot contain only numbers',
          input: ctx.value
        })
      }
    })
    // Block placeholder / disposable / generated-looking lead addresses using
    // the SHARED classifier — the same one the request-access wizard, the
    // `useEmailForm` hook, and the CRM upsert use. This replaces a weaker,
    // drifting `temp.`/`disposable.`-prefix check so the homepage CTA can't
    // submit a heuristic-only spam lead (e.g. `summer_bear42@noexpiremail.top`)
    // that would otherwise send a welcome email and show success before the CRM
    // gate silently rejected it (#525 r3530823856).
    .check((ctx) => {
      if (isLikelyLeadSpamEmail(ctx.value)) {
        ctx.issues.push({
          code: 'custom',
          message: 'Please enter a valid email address',
          input: ctx.value
        })
      }
    })
})

/**
 * Email form component for capturing user email addresses.
 * 
 * @description
 * Renders a form with email input and submit button. Handles:
 * - Email validation using Zod 4 schema
 * - Welcome email sending via Postmark
 * - CRM integration for lead capture
 * - Analytics event tracking
 * - User feedback via toast notifications
 * 
 * @remarks
 * Currently used exclusively on the homepage hero section.
 * All analytics events are tagged with 'homepage_hero' context.
 * 
 * @returns {JSX.Element} The rendered email form component
 */
const EmailForm = (): JSX.Element => {
  const { toast } = useToast()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ""
    }
  })

  /**
   * Tracks button click event for analytics.
   * 
   * @description
   * Fires when user clicks the "Get Early Access" button,
   * regardless of form validation state.
   */
  const handleButtonClick = (): void => {
    sendEvent({
      action: EventTypes.BUTTON_CLICK,
      label: "cta_waitlist_signup",
      page_location: "Homepage",
      element_id: "Early Access Button"
    })
  }

  /**
   * Handles form submission with email validation and processing.
   * 
   * @param {z.infer<typeof formSchema>} values - Validated form values
   * @returns {Promise<void>}
   * 
   * @description
   * Processing flow:
   * 1. Track signup attempt event
   * 2. Send welcome email via Postmark
   * 3. Add email to CRM (non-blocking)
   * 4. Track successful email capture
   * 5. Reset form and show success toast
   * 
   * @remarks
   * CRM failures are logged but don't block the user flow.
   * All errors are caught and displayed via toast notifications.
   */
  const onSubmit = async (values: z.infer<typeof formSchema>): Promise<void> => {
    // Track signup attempt for funnel analysis
    sendEvent({
      action: EventTypes.SIGNUP_ATTEMPT,
      label: "Signup Attempt",
      page_location: "Email Form",
      element_id: "Email Signup Form"
    })

    try {
      // Send welcome email using Postmark template. `sendEmail` derives the spam
      // screen from the template server-side — the `welcome` (lead) template is
      // heuristic-screened, so a bypassed client can't push heuristic-only spam
      // to Postmark ahead of the CRM gate (#525 r3531109535).
      await sendEmail({
        to: values.email,
        from: "info@adsights.ai",
        templateAlias: "welcome",
        templateModel: {
          name: values.email.split("@")[0], // Using the part before @ as the name
          sender_name: "The AdSights Team",
          product_name: "AdSights",
          product_url: "https://www.adsights.ai",
          logo_url: "https://adsights.ai/logo-square.png",
          company_name: "AdSights Inc.",
          company_address: "254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702",
          support_email: "support@adsights.ai"
        }
      })


      // Add to CRM (non-blocking - failures are logged but don't stop flow)
      try {
        await addToCRM(values.email, "homepage_waitlist_form")
      } catch (crmError: unknown) {
        logClientError(crmError, { component: 'EmailForm', context: 'Failed to add to CRM' })
        // CRM failure doesn't block user experience
      }

      // Reset form to initial state
      form.reset()
      
      // Show success feedback to user
      toast({
        variant: "success",
        title: "Success",
        description: "Check your email for a confirmation message."
      })
      
      // Track email capture with detailed context for analytics
      // NOTE: This form is currently only used on the homepage hero section
      trackEmailCapture({
        captureContext: 'homepage_hero',
        sourcePage: 'homepage',
        sourceElement: 'hero_email_form'
      })
      
      // Track form submission event with standard parameters
      sendEvent({
        action: "form_submission",
        label: "Email Waitlist Form",
        value: 1,
        page_location: window.location.href,
        element_id: "Email Waitlist Form"
      })
    } catch (error: unknown) {
      logClientError(error, { component: 'EmailForm', context: 'Email form submission failed' })
      
      // Show error feedback to user with support contact
      toast({
        variant: "error",
        title: "Error",
        description:
          "Failed to send email. Please try again or contact support@adsights.ai"
      })
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col items-center gap-3 sm:flex-row">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="h-10 w-full flex-auto">
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  required
                  id="email"
                  className="h-10 w-full flex-auto"
                  inputClassName="h-full"
                  placeholder="Your Work Email"
                  {...field}
                />
              </FormControl>
              {/* FormMessage intentionally omitted - validation errors shown via toast */}
            </FormItem>
          )}
        />
        <Button
          onClick={handleButtonClick}
          className="h-10 w-full sm:w-fit sm:flex-none"
          type="submit"
          variant="primary">
          Get Early Access
        </Button>
      </form>
    </Form>
  )
}

export default EmailForm